import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthSession } from "../../auth/keycloak.js";
import { favoriteApi } from "../../api/client.js";

const EMPTY: ReadonlySet<number> = new Set();

/**
 * The caller's favourite facility ids (persisted — F1). GET on mount + **optimistic** add/remove. Keyed by tenant +
 * subject (per-user, like {@link useFacilities}). The server already filters the list to currently-visible facilities;
 * the drill-down maps these ids onto its loaded tree and skips any that don't resolve (defence-in-depth, panel #6).
 */
export function useFacilityFavourites(session: AuthSession | null) {
  const qc = useQueryClient();
  const key = ["facility-favourites", session?.realm, session?.sub] as const;
  // Per-facility serialization + explicit desired-state so rapid add-then-remove can't commit out of order (panel #2).
  const chains = useRef(new Map<number, Promise<unknown>>());
  const desired = useRef(new Map<number, boolean>());

  const query = useQuery({
    queryKey: key,
    enabled: session !== null,
    queryFn: async () => {
      const res = await favoriteApi.listUserFavourites();
      if (res.status !== 200) throw new Error(`GET /user/favourites returned ${res.status}`);
      return new Set(res.body.facilities);
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ id, next }: { id: number; next: boolean }) => {
      const res = next
        ? await favoriteApi.addFacilityFavourite({ params: { id }, body: {} })
        : await favoriteApi.removeFacilityFavourite({ params: { id } });
      if (res.status !== 200) throw new Error(`favourite ${next ? "add" : "remove"} returned ${res.status}`);
    },
    // Optimistic — flip ONLY this id; on error revert ONLY this id (never restore the whole snapshot, which would
    // clobber a concurrent toggle of a DIFFERENT facility); reconcile on settle. Works from a cold cache too.
    onMutate: async ({ id, next }) => {
      await qc.cancelQueries({ queryKey: key });
      const had = qc.getQueryData<ReadonlySet<number>>(key)?.has(id) ?? false;
      qc.setQueryData<ReadonlySet<number>>(key, (curr) => {
        const s = new Set(curr ?? EMPTY);
        if (next) s.add(id);
        else s.delete(id);
        return s;
      });
      return { had };
    },
    onError: (_e, vars, ctx) => {
      if (!ctx) return;
      qc.setQueryData<ReadonlySet<number>>(key, (curr) => {
        const s = new Set(curr ?? EMPTY);
        if (ctx.had) s.add(vars.id);
        else s.delete(vars.id);
        return s;
      });
    },
    onSettled: (_d, _e, vars) => {
      // Clear the desired-state tracker ONLY when this settled mutation IS the last-intended one — else a still-queued
      // toggle for the same facility would fall back to the mid-flight cache and enqueue the wrong action.
      if (desired.current.get(vars.id) === vars.next) desired.current.delete(vars.id);
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  const favourites = query.data ?? EMPTY;
  return {
    favourites,
    /** The read's state — `favourites` is EMPTY while pending AND after a failure, so a consumer that must not show
     *  "no favourites" for a failed read (the Overview card) gates on these. */
    isPending: query.isPending,
    isError: query.isError,
    refetch: () => void query.refetch(),
    /** Toggle a facility's favourite (optimistic). Serialized per facility + tracks the DESIRED final state, so a rapid
     *  add-then-remove commits in order and lands in the last-clicked state (panel #2). */
    toggle: (id: number) => {
      const next = !(desired.current.get(id) ?? favourites.has(id));
      desired.current.set(id, next);
      const prev = chains.current.get(id) ?? Promise.resolve();
      chains.current.set(id, prev.catch(() => {}).then(() => mutation.mutateAsync({ id, next })));
    },
  };
}
